import React, { useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";

import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import {
  computeHarvestLossFromSeedCount,
  DEFAULT_KG_PER_BAG,
  DEFAULT_MIN_REPETITIONS,
  DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT,
  computeHarvestLossAlertLimit,
  HARVEST_LOSS_SAMPLE_AREA_M2,
  HARVEST_LOSS_SAMPLE_LENGTH_CM,
  HARVEST_LOSS_SAMPLE_WIDTH_CM,
} from "../FieldApp/harvestLossMetrics";
import {
  HARVEST_LOSS_ALERT_HELP_TITLE,
  HarvestLossAlertHelpContent,
} from "./harvestLossAlertHelpContent";
import {
  HARVEST_LOSS_SAMPLING_HELP_TITLE,
  HarvestLossSamplingHelpContent,
} from "./harvestLossSamplingHelpContent";
import { parsePositiveNumber } from "./serviceOrderProducts";
import "./ServiceOrderHarvestLossPanel.css";

function ServiceOrderHarvestLossPanel({
  pmsG,
  onPmsGChange,
  toleratedLossKgHa,
  onToleratedLossKgHaChange,
  alertThresholdPercent,
  onAlertThresholdPercentChange,
  kgPerBag,
  onKgPerBagChange,
  minRepetitions,
  onMinRepetitionsChange,
  previewSeedCount,
  onPreviewSeedCountChange,
  t,
}) {
  const [showSamplingHelp, setShowSamplingHelp] = useState(false);
  const [showAlertHelp, setShowAlertHelp] = useState(false);
  const samplingHelpTitle = t(HARVEST_LOSS_SAMPLING_HELP_TITLE);
  const alertHelpTitle = t(HARVEST_LOSS_ALERT_HELP_TITLE);
  const toleratedValue = parsePositiveNumber(toleratedLossKgHa);
  const alertThresholdValue =
    parsePositiveNumber(alertThresholdPercent) ||
    DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT;
  const alertLimitKgHa =
    toleratedValue > 0
      ? computeHarvestLossAlertLimit(toleratedValue, alertThresholdValue)
      : null;

  const preview = useMemo(() => {
    const pms = parsePositiveNumber(pmsG);
    const seeds = parsePositiveNumber(previewSeedCount);
    const bagKg = parsePositiveNumber(kgPerBag) || DEFAULT_KG_PER_BAG;
    if (!pms || seeds == null) return null;
    return computeHarvestLossFromSeedCount({
      seedCount: seeds,
      pmsG: pms,
      kgPerBag: bagKg,
    });
  }, [pmsG, previewSeedCount, kgPerBag]);

  function AlertFieldHint({ children }) {
    return (
      <p className="service-order-harvest-loss-field-hint">
        {children}{" "}
        <button
          type="button"
          className="service-order-harvest-loss-info-btn"
          title={alertHelpTitle}
          aria-label={alertHelpTitle}
          onClick={() => setShowAlertHelp(true)}
        >
          <FaInfoCircle aria-hidden />
        </button>
      </p>
    );
  }

  return (
    <div className="service-order-harvest-loss-panel">
      <div className="service-order-harvest-loss-intro">
        <strong>{t("Ferramenta de perda de colheita")}</strong>
        <p>
          {t(
            "Configure o PMS e a margem tolerada. No app, o operador contará sementes em quadrados de {{width}}×{{length}} cm ({{area}} m²) e registrará pelo menos três repetições para calcular kg/ha e sacas/ha.",
            {
              width: HARVEST_LOSS_SAMPLE_WIDTH_CM,
              length: HARVEST_LOSS_SAMPLE_LENGTH_CM,
              area: HARVEST_LOSS_SAMPLE_AREA_M2.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }
          )}
        </p>
      </div>

      <div className="service-order-harvest-loss-grid">
        <div className="create-schedule-field">
          <label htmlFor="harvest-loss-pms">{t("PMS — peso de mil sementes (g)")} *</label>
          <input
            id="harvest-loss-pms"
            type="text"
            inputMode="decimal"
            className="form-control"
            value={pmsG}
            onChange={(event) => onPmsGChange(event.target.value)}
            placeholder="Ex.: 180"
            required
          />
        </div>

        <div className="create-schedule-field">
          <label htmlFor="harvest-loss-tolerance">
            {t("Margem de perda tolerada (kg/ha)")}
          </label>
          <input
            id="harvest-loss-tolerance"
            type="text"
            inputMode="decimal"
            className="form-control"
            value={toleratedLossKgHa}
            onChange={(event) => onToleratedLossKgHaChange(event.target.value)}
            placeholder="Ex.: 50"
          />
        </div>

        <div className="create-schedule-field">
          <label htmlFor="harvest-loss-alert-threshold">
            {t("Alerta acima da margem (%)")}
          </label>
          <input
            id="harvest-loss-alert-threshold"
            type="text"
            inputMode="decimal"
            className="form-control"
            value={alertThresholdPercent}
            onChange={(event) => onAlertThresholdPercentChange(event.target.value)}
            placeholder={String(DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT)}
          />
          {alertLimitKgHa != null ? (
            <AlertFieldHint>
              {t(
                "Gera alerta na plataforma quando a amostra ultrapassar {{limit}} kg/ha (margem + {{percent}}%).",
                {
                  limit: alertLimitKgHa.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                  percent: alertThresholdValue.toLocaleString("pt-BR", {
                    maximumFractionDigits: 0,
                  }),
                }
              )}
            </AlertFieldHint>
          ) : (
            <AlertFieldHint>
              {t(
                "Informe a margem tolerada para calcular o limite de alerta enviado ao painel de ações pendentes."
              )}
            </AlertFieldHint>
          )}
        </div>

        <div className="create-schedule-field">
          <label htmlFor="harvest-loss-bag">{t("Peso da saca (kg)")}</label>
          <input
            id="harvest-loss-bag"
            type="text"
            inputMode="decimal"
            className="form-control"
            value={kgPerBag}
            onChange={(event) => onKgPerBagChange(event.target.value)}
            placeholder={String(DEFAULT_KG_PER_BAG)}
          />
        </div>

        <div className="create-schedule-field">
          <label htmlFor="harvest-loss-repetitions">
            {t("Repetições mínimas")}
          </label>
          <input
            id="harvest-loss-repetitions"
            type="number"
            min={1}
            max={20}
            className="form-control"
            value={minRepetitions}
            onChange={(event) => onMinRepetitionsChange(event.target.value)}
          />
        </div>
      </div>

      <div className="service-order-harvest-loss-preview">
        <label htmlFor="harvest-loss-preview-count">
          {t("Simulação — sementes em {{width}}×{{length}} cm", {
            width: HARVEST_LOSS_SAMPLE_WIDTH_CM,
            length: HARVEST_LOSS_SAMPLE_LENGTH_CM,
          })}
        </label>
        <input
          id="harvest-loss-preview-count"
          type="text"
          inputMode="numeric"
          className="form-control"
          value={previewSeedCount}
          onChange={(event) => onPreviewSeedCountChange(event.target.value)}
          placeholder={t("Quantidade encontrada na amostra")}
        />
        {preview && preview.kgPerHa != null ? (
          <p className="service-order-harvest-loss-preview-result">
            {t("Perda estimada")}:{" "}
            <strong>{preview.kgPerHa.toFixed(2)} kg/ha</strong>
            {preview.bagsPerHa != null ? (
              <>
                {" "}
                · <strong>{preview.bagsPerHa.toFixed(3)} sc/ha</strong>
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      <p className="service-order-harvest-loss-note">
        {t(
          "Observação enviada ao operador: realizar pelo menos {{count}} repetições em diferentes pontos do talhão, sempre em área de {{width}}×{{length}} cm ({{area}} m²), e registrar cada amostra no app.",
          {
            count: minRepetitions || DEFAULT_MIN_REPETITIONS,
            width: HARVEST_LOSS_SAMPLE_WIDTH_CM,
            length: HARVEST_LOSS_SAMPLE_LENGTH_CM,
            area: HARVEST_LOSS_SAMPLE_AREA_M2.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          }
        )}{" "}
        <button
          type="button"
          className="service-order-harvest-loss-info-btn"
          title={samplingHelpTitle}
          aria-label={samplingHelpTitle}
          onClick={() => setShowSamplingHelp(true)}
        >
          <FaInfoCircle aria-hidden />
        </button>
      </p>

      {showAlertHelp && (
        <SidebarToolInfoModal
          title={alertHelpTitle}
          onClose={() => setShowAlertHelp(false)}
        >
          <HarvestLossAlertHelpContent
            toleratedKgHa={toleratedValue > 0 ? toleratedValue : null}
            alertThresholdPercent={alertThresholdValue}
            alertLimitKgHa={alertLimitKgHa}
          />
        </SidebarToolInfoModal>
      )}

      {showSamplingHelp && (
        <SidebarToolInfoModal
          title={samplingHelpTitle}
          onClose={() => setShowSamplingHelp(false)}
        >
          <HarvestLossSamplingHelpContent />
        </SidebarToolInfoModal>
      )}
    </div>
  );
}

export default ServiceOrderHarvestLossPanel;
